import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import styled from '@emotion/styled';

import { useQuery } from '@apollo/client/react';

import { FontWeight } from '@clutter/clean';

import { GeocodeDocument, RateGroupKindEnum } from '@graphql/platform';

import { useSharedCheckoutContext } from '@root/components/checkout/context';
import { DIMENSIONAL_PLANS, PLANS } from '@root/constants/pricing';
import { useTrack } from '@root/initializers/wt';
import { Commitment as CommitmentType } from '@root/resources/types/commitment';
import { PlanName } from '@root/resources/types/plan_name';
import { EventSchema } from '@root/resources/wt/types';

import { formatCurrency } from '@utils/formatters';
import { useStoragePriceCompletedFunnelEvent } from '@utils/hooks/funnel_events/use_storage_price_completed_funnel_event';
import { useTrackFunnelEvents } from '@utils/hooks/funnel_events/use_track_funnel_event';
import { parseFreeDuration } from '@utils/hooks/pricing/use_pricing_summary';
import { useLastDefined } from '@utils/hooks/use_last_defined';
import { extractStoragePrice } from '@utils/pricing/pricing_set';

import recommendCheck from '@images/checkout/challenger/recommend_check.svg';
import unit5x5 from '@images/checkout/challenger/unit_5x5.png';
import unit5x10 from '@images/checkout/challenger/unit_5x10.png';
import unit10x10 from '@images/checkout/challenger/unit_10x10.png';
import unit10x15 from '@images/checkout/challenger/unit_10x15.png';
import unit10x20 from '@images/checkout/challenger/unit_10x20.png';
import unit10x25 from '@images/checkout/challenger/unit_10x25.png';
import unit10x30 from '@images/checkout/challenger/unit_10x30.png';
import unit10x40 from '@images/checkout/challenger/unit_10x40.png';
import unit10x50 from '@images/checkout/challenger/unit_10x50.png';

import { FinalDeliveryCostModal } from '../final_delivery_cost_modal';
import { commitmentDiscountPercents } from '../hooks/commitment_discounts';
import { useChallengerBreakpoints } from '../hooks/use_challenger_breakpoints';
import {
  onboardingSummariesForPlan,
  useChallengerOnboardingCostSummaries,
} from '../hooks/use_challenger_onboarding_cost_summaries';
import { challengerPlanId } from '../hooks/use_challenger_plan_pricing_cache';
import { useSliderPlanSize } from '../hooks/use_slider_plan_size';
import { TABLET_UP as DESKTOP, MOBILE } from '../media';
import {
  Banner,
  BannerTitle,
  CardPadded,
  FeatureCheck,
  HelpText,
  InlineBrandLink,
  Pill,
  Question,
} from '../shared';
import {
  selectedItemsFromStorageCalculatorItems,
  storageCalculatorItemsFromSelectedItems,
} from '../storage_calculator_items';
import { StorageCalculatorModal } from '../storage_calculator_modal';
import { CHALLENGER as T } from '../tokens';

/** Figma unit-size illustrations (314×240 frame). */
const PLAN_ILLUSTRATION: Record<PlanName, string> = {
  '5x5': unit5x5,
  '5x10': unit5x10,
  '10x10': unit10x10,
  '10x15': unit10x15,
  '10x20': unit10x20,
  '10x25': unit10x25,
  '10x30': unit10x30,
  '10x40': unit10x40,
  '10x50': unit10x50,
};

/**
 * Figma 707:20077 — compact 3 labels (start / center / end); slider still steps all plans.
 * Used below desktop (mobile + tablet): at 768px the summary column leaves ~300px
 * for the track, so the denser desktop ticks overlap.
 */
const MOBILE_SLIDER_LABEL_NAMES: PlanName[] = ['5x5', '10x20', '10x50'];
/** Figma 338:2472 / 707:10088 — thumb pill on the track. */
const SLIDER_THUMB_WIDTH_PX = 32;
const SLIDER_THUMB_HALF_PX = SLIDER_THUMB_WIDTH_PX / 2;
const SLIDER_TRACK_RADIUS = T.radius.xs;

/** Thumb-center x offset; inset by half pill width so end ticks align with pill centers. */
function sliderThumbCenterOffset(planIndex: number, planCount: number): string {
  if (planCount <= 1) return `${SLIDER_THUMB_HALF_PX}px`;
  const ratio = planIndex / (planCount - 1);
  return `calc(${SLIDER_THUMB_HALF_PX}px + (100% - ${SLIDER_THUMB_WIDTH_PX}px) * ${ratio})`;
}

function sliderThumbPlacement(planIndex: number, planCount: number) {
  const atStart = planIndex <= 0;
  const atEnd = planCount > 0 && planIndex >= planCount - 1;
  const centerOffset = sliderThumbCenterOffset(planIndex, planCount);
  return { atStart, atEnd, centerOffset };
}

/** Figma 338:2592 / 338:2594 / 338:2697 — second bullet varies by commitment. */
function additionalPickupCopy(commitment: CommitmentType): string {
  switch (commitment) {
    case RateGroupKindEnum.Saver:
      return '4 additional pickups and/or deliveries with complimentary hours';
    case RateGroupKindEnum.Flexer:
      return '1 additional pickup or delivery with complimentary hours';
    case RateGroupKindEnum.Mover:
    default:
      return 'Additional pickups and deliveries available for purchase';
  }
}
/** Figma 27:2989 — feature rows under What’s included. */
const InclusionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${T.space[8]};
  width: 100%;
`;

const COMMITMENTS: Array<{
  value: CommitmentType;
  label: string;
}> = [
  { value: RateGroupKindEnum.Saver, label: '8-month minimum' },
  { value: RateGroupKindEnum.Flexer, label: '4-month minimum' },
  { value: RateGroupKindEnum.Mover, label: 'Month to month' },
];

const HeaderBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${T.space[8]};
  width: 100%;
`;

/** Figma 144:2648 / 29:3382 — Body/Medium 400, wrap like canvas text. */
const DurationHelpText = styled(HelpText)`
  word-break: break-word;

  ${MOBILE} {
    font-size: 14px;
    line-height: 20px;
  }
`;

/**
 * Inline span (not a button box) so wrap can fall after “storage”, matching
 * Figma 144:2648 mobile and 29:3382 desktop — “calculator to help…” stays
 * together on the next line.
 */
const DurationBrandLink = styled.span`
  color: ${T.color.linkBrand};
  cursor: pointer;
  font-family: ${T.font.sans};
  font-size: 14px;
  font-style: normal;
  font-weight: ${FontWeight.Semibold};
  line-height: 19px;
  word-break: break-word;

  ${MOBILE} {
    line-height: 20px;
  }

  &:hover {
    opacity: 0.85;
  }
`;

/**
 * Figma Banner-small 936:16447 — recommendation after storage calculator.
 * summarySurface, pad 12, gap 8, icon-with-bg + Demi Bold title / Regular body.
 */
const RecommendBanner = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${T.space[8]};
  width: 100%;
  padding: ${T.space[12]};
  box-sizing: border-box;
  background: ${T.color.summarySurface};
  border-radius: ${T.radius.md};
`;

/** Figma icon-with-bg: 28×28 white shell + brand-25 fill, 20×20 glyph slot. */
const RecommendBadge = styled.span`
  position: relative;
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 4px;
  box-sizing: border-box;
  border-radius: ${T.radius.sm};
  background: ${T.color.bgPrimary};

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: ${T.radius.sm};
    background: rgba(8, 170, 168, 0.25);
  }
`;

const RecommendIconBox = styled.span`
  position: relative;
  z-index: 1;
  display: block;
  width: 20px;
  height: 20px;
  overflow: hidden;
  flex-shrink: 0;
`;

/** Figma icon/done leaf inset 22.08% / 13.33% inside 20×20. */
const RecommendIcon = styled.img`
  position: absolute;
  top: 4.42px;
  left: 2.67px;
  width: 14.67px;
  height: 11.17px;
  display: block;
`;

const RecommendCopy = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  font-family: ${T.font.sans};
  color: ${T.color.textPrimary};
`;

const RecommendTitle = styled.div`
  font-size: 14px;
  font-weight: ${FontWeight.Semibold};
  line-height: 19px;
`;

const RecommendBody = styled.div`
  font-size: 14px;
  font-weight: ${FontWeight.Regular};
  line-height: 20px;
`;

const CommitmentRow = styled.div`
  display: flex;
  width: 100%;

  ${MOBILE} {
    flex-direction: column;
    gap: 4px;
  }

  ${DESKTOP} {
    flex-direction: row;
    gap: ${T.space[16]};
  }
`;

const CommitmentOption = styled.button<{ selected?: boolean }>`
  appearance: none;
  min-width: 0;
  cursor: pointer;
  text-align: left;
  background: ${({ selected }) =>
    selected ? T.color.bgSelected : T.color.bgPrimary};
  border: 1px solid
    ${({ selected }) =>
      selected ? T.color.borderBrand : T.color.borderDefault};
  border-radius: ${T.radius.md};
  padding: ${T.space[12]} 0;
  font-family: ${T.font.sans};

  ${MOBILE} {
    flex: none;
    width: 100%;
  }

  ${DESKTOP} {
    flex: 1 0 0;
  }
`;

const CommitmentInner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${T.space[4]};

  ${MOBILE} {
    padding: 0 ${T.space[12]};
  }

  ${DESKTOP} {
    padding: 0 ${T.space[16]};
  }
`;

const CommitmentLabel = styled.span`
  font-weight: ${FontWeight.Medium};
  color: ${T.color.textPrimary};
  text-align: left;

  ${MOBILE} {
    font-size: 13px;
    line-height: 18px;
  }

  ${DESKTOP} {
    font-size: 14px;
    line-height: 21px;
  }
`;

const IllustrationWrap = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${T.space[16]} 0;
  box-sizing: border-box;

  ${MOBILE} {
    height: 224px;
  }

  ${DESKTOP} {
    height: 280px;
  }
`;

/** Desktop Figma 314×240; mobile scales inside 224px wrap. */
const IllustrationFrame = styled.div`
  position: relative;
  flex-shrink: 0;

  ${MOBILE} {
    width: 100%;
    max-width: 280px;
    height: 192px;
  }

  ${DESKTOP} {
    width: 314px;
    height: 240px;
  }
`;

const IllustrationImg = styled.img`
  display: block;
  object-fit: contain;

  ${MOBILE} {
    width: 100%;
    height: 192px;
  }

  ${DESKTOP} {
    width: 314px;
    height: 240px;
  }
`;

const PriceBlock = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${T.space[4]};
  padding: ${T.space[12]} 0;
`;

const Price = styled.div`
  font-weight: ${FontWeight.Medium};
  color: ${T.color.textPrimary};

  ${MOBILE} {
    font-size: 16px;
    line-height: 22px;
  }

  ${DESKTOP} {
    font-size: 20px;
    line-height: 24px;
  }
`;

const PlanCaption = styled.div`
  font-weight: ${FontWeight.Regular};
  color: ${T.color.textPrimary};

  ${MOBILE} {
    font-size: 13px;
    line-height: 18px;
  }

  ${DESKTOP} {
    font-size: 14px;
    line-height: 20px;
  }
`;

const SliderWrap = styled.div`
  position: relative;
  width: 100%;
  padding-bottom: ${T.space[48]};

  ${DESKTOP} {
    /* Room for teal size helper above the thumb (Funnel-Feedback 29:3429). */
    padding-top: 20px;
  }
`;

const SliderTrack = styled.div`
  position: relative;
  width: 100%;
  height: 8px;
`;

/** Rounded gray rail; fill clips to pill-shaped caps. */
const SliderTrackRail = styled.div`
  position: absolute;
  inset: 0;
  border-radius: ${SLIDER_TRACK_RADIUS};
  background: ${T.color.bgButtonDisabled};
  overflow: hidden;
`;

/**
 * Figma 337:2346 / 338:3161 — ticks centered on thumb stops (inset from track ends).
 */
const SliderTickMarks = styled.div`
  position: absolute;
  inset: 0;
  z-index: 1;
  pointer-events: none;
`;

const SLIDER_TICK_WITHIN_FILL = 'rgba(249, 250, 251, 0.7)';

/**
 * Figma 337:2346 / 431:2200 — ticks on gray rail use text-primary; ticks within
 * the teal fill use #F9FAFB at 70%.
 */
const SliderTickMark = styled.div<{ $offset: string; $withinFill?: boolean }>`
  position: absolute;
  top: 0;
  left: ${({ $offset }) => $offset};
  transform: translateX(-50%);
  width: 2px;
  height: 8px;
  background: ${({ $withinFill }) =>
    $withinFill ? SLIDER_TICK_WITHIN_FILL : T.color.textPrimary};
`;

/** Figma 338:3188 — teal fill to thumb center with rounded leading edge. */
const SliderFill = styled.div<{ $width: string }>`
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  z-index: 0;
  width: ${({ $width }) => $width};
  box-sizing: border-box;
  background: ${T.color.iconBrand};
  border-radius: ${SLIDER_TRACK_RADIUS};
`;

/**
 * Figma 338:3190 — 5×5 flush left; last plan flush right; others on tick centers.
 */
const SliderThumb = styled.div<{
  $centerOffset: string;
  $atStart?: boolean;
  $atEnd?: boolean;
}>`
  position: absolute;
  top: 50%;
  left: ${({ $atStart, $atEnd, $centerOffset }) =>
    $atStart ? '0' : $atEnd ? '100%' : $centerOffset};
  z-index: 2;
  transform: ${({ $atStart, $atEnd }) =>
    $atStart
      ? 'translate(0, -50%)'
      : $atEnd
        ? 'translate(-100%, -50%)'
        : 'translate(-50%, -50%)'};
  width: ${SLIDER_THUMB_WIDTH_PX}px;
  height: 16px;
  border-radius: ${T.radius.pill};
  background: ${T.color.bgPrimary};
  border: 1px solid ${T.color.bgPrimary};
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.18);
  pointer-events: none;
`;

/** Full-track range; discrete steps map to tick centers via visual pct. */
const SliderInput = styled.input`
  position: absolute;
  inset: 0;
  z-index: 3;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
  margin: 0;
`;

/**
 * Aligns a label/bubble to the thumb center.
 */
const thumbCenterStyle = (
  centerOffset: string,
  atStart?: boolean,
  atEnd?: boolean,
) => `
  left: ${
    atStart
      ? `${SLIDER_THUMB_HALF_PX}px`
      : atEnd
        ? `calc(100% - ${SLIDER_THUMB_HALF_PX}px)`
        : centerOffset
  };
  transform: translate(-50%, 0);
`;

/** Funnel-Feedback 29:3429 — desktop teal size helper above the thumb. */
const DesktopSizeHint = styled.div<{
  $centerOffset: string;
  $atStart?: boolean;
  $atEnd?: boolean;
}>`
  display: none;

  ${DESKTOP} {
    display: block;
    position: absolute;
    top: 0;
    ${({ $centerOffset, $atStart, $atEnd }) =>
      thumbCenterStyle($centerOffset, $atStart, $atEnd)};
    z-index: 2;
    font-family: ${T.font.sans};
    font-size: 12px;
    font-weight: ${FontWeight.Medium};
    line-height: 16px;
    color: ${T.color.textBrand};
    text-align: center;
    white-space: nowrap;
    pointer-events: none;
  }
`;

const TickLabels = styled.div`
  position: relative;
  margin-top: 20px;
  width: 100%;
  height: 16px;
  overflow: visible;
  font-size: 12px;
  font-weight: ${FontWeight.Medium};
  line-height: 16px;
  color: ${T.color.textTertiary};

  ${MOBILE} {
    height: 14px;
    font-size: 10px;
    line-height: 14px;
  }
`;

const Tick = styled.span<{ $offset: string }>`
  position: absolute;
  top: 0;
  left: ${({ $offset }) => $offset};
  transform: translateX(-50%);
  white-space: nowrap;
`;

/** Desktop 24; mobile Storage options (1052:19914) uses 12 between sections. */
const Stack = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;

  ${MOBILE} {
    gap: ${T.space[12]};
  }

  ${DESKTOP} {
    gap: ${T.space[24]};
  }
`;

type Props = {
  planSize?: PlanName;
  commitment?: CommitmentType;
  onPlanSizeChange: (plan: PlanName) => void;
  onCommitmentChange: (commitment: CommitmentType) => void;
  /** Fired when the slider thumb moves before flow state commits on release. */
  onDisplayPlanSizeChange?: (plan: PlanName | undefined) => void;
};

export const PlanAndCommitmentSection: React.FC<Props> = ({
  planSize,
  commitment,
  onPlanSizeChange,
  onCommitmentChange,
  onDisplayPlanSizeChange,
}) => {
  const [showEstimator, setShowEstimator] = useState(false);
  const [showFinalDeliveryCost, setShowFinalDeliveryCost] = useState(false);
  const [recommendedPlan, setRecommendedPlan] = useState<PlanName | null>(null);
  const track = useTrack({ action: 'click' });
  const trackFunnelEvent = useTrackFunnelEvents();
  const trackStoragePriceCompleted = useStoragePriceCompletedFunnelEvent();
  const pricedOnceRef = useRef(false);

  const openStorageCalculator = () => {
    setShowEstimator(true);
    track({
      objectName: 'plan_size_selector',
      label: 'Storage calculator',
      objectType: 'button',
    });
  };
  const {
    pricingSummary: summary,
    defaultPricingSummary,
    pricingSet,
    flowState: { values, onChange: onFlowChange },
  } = useSharedCheckoutContext();

  const handleFlowPlanSizeChange = useCallback(
    (next: PlanName) => {
      onPlanSizeChange(next);
      if (commitment && pricingSet) {
        const amount = extractStoragePrice(commitment, next, pricingSet);
        if (amount != null) {
          pricedOnceRef.current = true;
          trackStoragePriceCompleted({
            planSize: next,
            commitment,
            amount,
          });
        }
      }
    },
    [commitment, onPlanSizeChange, pricingSet, trackStoragePriceCompleted],
  );

  const {
    displayPlanSize,
    setPlanSizeFromSlider,
    setPlanSizeImmediate,
    commitPlanSize,
  } = useSliderPlanSize(planSize, handleFlowPlanSizeChange);

  useEffect(() => {
    onDisplayPlanSizeChange?.(displayPlanSize);
  }, [displayPlanSize, onDisplayPlanSizeChange]);

  const { data: geocodeData } = useQuery(GeocodeDocument, {
    variables: { zip: values.zip! },
    skip: !values.zip,
  });
  const regionName = geocodeData?.geocode?.region?.name;
  const serviceAreaLabel =
    regionName && values.zip
      ? `${regionName} through ${values.zip}`
      : 'your service area';
  const pricingSummary = useLastDefined(summary) ?? defaultPricingSummary;
  const currentPlanId = challengerPlanId(displayPlanSize, pricingSet);
  const { data: onboardingCostData } =
    useChallengerOnboardingCostSummaries(pricingSet);
  const onboardingCostSummariesForPlan = useMemo(
    () => onboardingSummariesForPlan(onboardingCostData?.result, currentPlanId),
    [currentPlanId, onboardingCostData?.result],
  );
  const complimentaryHoursDisplay = useMemo(() => {
    if (!onboardingCostSummariesForPlan) return '--';
    const { freeDuration } = parseFreeDuration(
      onboardingCostSummariesForPlan,
      commitment,
    );
    return freeDuration != null ? Math.round(freeDuration) : '--';
  }, [onboardingCostSummariesForPlan, commitment]);
  const storageCalculatorItems = useMemo(
    () =>
      selectedItemsFromStorageCalculatorItems(values.storageCalculatorItems),
    [values.storageCalculatorItems],
  );
  const storagePriceViewedRef = useRef(false);

  useEffect(() => {
    if (!pricingSet || storagePriceViewedRef.current) return;

    storagePriceViewedRef.current = true;
    trackFunnelEvent({
      schema: EventSchema.WWW__StoragePriceViewed,
      action: 'display',
      metadata: {},
    });
  }, [pricingSet]);

  const { isDesktop } = useChallengerBreakpoints();
  const useMobileLabels = !isDesktop;

  const planCount = DIMENSIONAL_PLANS.length;
  const planIndex = Math.max(
    0,
    DIMENSIONAL_PLANS.findIndex((p) => p.name === displayPlanSize),
  );
  const selectedPlan = DIMENSIONAL_PLANS[planIndex] ?? DIMENSIONAL_PLANS[0];
  const thumbPlacement = sliderThumbPlacement(planIndex, planCount);

  const monthlyAmount = useMemo(() => {
    if (!commitment || !selectedPlan || !pricingSet) {
      return pricingSummary?.monthlyAmount;
    }
    return (
      extractStoragePrice(commitment, selectedPlan.name, pricingSet) ??
      pricingSummary?.monthlyAmount
    );
  }, [commitment, selectedPlan, pricingSet, pricingSummary]);

  /**
   * Commitment pill % off month-to-month — from ZIP pricing set + selected size
   * (updates when ZIP or slider/calculator size changes; not hardcoded).
   */
  const discountByCommitment = useMemo(
    () => commitmentDiscountPercents(selectedPlan.name, pricingSet),
    [selectedPlan.name, pricingSet],
  );

  // Classic fires storage_price_completed on plan + commitment select. Challenger
  // pre-selects defaults, so also fire once when price first resolves so funnel
  // drop-off can be compared without requiring an extra slider drag.
  useEffect(() => {
    if (
      pricedOnceRef.current ||
      !planSize ||
      !commitment ||
      monthlyAmount == null
    ) {
      return;
    }
    pricedOnceRef.current = true;
    trackStoragePriceCompleted({
      planSize,
      commitment,
      amount: monthlyAmount,
    });
  }, [planSize, commitment, monthlyAmount, trackStoragePriceCompleted]);

  const trackPriceCompleted = (
    nextPlan: PlanName,
    nextCommitment: CommitmentType,
  ) => {
    if (!pricingSet) return;
    const amount = extractStoragePrice(nextCommitment, nextPlan, pricingSet);
    if (amount == null) return;
    pricedOnceRef.current = true;
    trackStoragePriceCompleted({
      planSize: nextPlan,
      commitment: nextCommitment,
      amount,
    });
  };

  const additionalPickupLine = commitment
    ? additionalPickupCopy(commitment)
    : null;

  return (
    <CardPadded>
      <Stack>
        <HeaderBlock>
          <Question>How long and how much storage do you need?</Question>
          <DurationHelpText>
            If your items require more or less space than you reserve, your rate
            will be adjusted. Use our{' '}
            <DurationBrandLink
              role="button"
              tabIndex={0}
              onClick={openStorageCalculator}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  openStorageCalculator();
                }
              }}
            >
              storage
              <br />
              calculator
            </DurationBrandLink>{' '}
            to help you estimate how much space to reserve.
          </DurationHelpText>
        </HeaderBlock>

        <CommitmentRow>
          {COMMITMENTS.map((option) => {
            const selected = commitment === option.value;
            const discount = discountByCommitment[option.value];
            return (
              <CommitmentOption
                key={option.value}
                type="button"
                selected={selected}
                onClick={() => {
                  track({
                    objectName: 'commitment_card',
                    objectType: 'button',
                    label: option.label,
                    value: option.value,
                  });
                  if (displayPlanSize) {
                    trackPriceCompleted(displayPlanSize, option.value);
                  }
                  onCommitmentChange(option.value);
                }}
              >
                <CommitmentInner>
                  <CommitmentLabel>{option.label}</CommitmentLabel>
                  {discount != null && <Pill>-{discount}%</Pill>}
                </CommitmentInner>
              </CommitmentOption>
            );
          })}
        </CommitmentRow>

        {recommendedPlan && (
          <RecommendBanner>
            <RecommendBadge>
              <RecommendIconBox>
                <RecommendIcon src={recommendCheck} alt="" />
              </RecommendIconBox>
            </RecommendBadge>
            <RecommendCopy>
              <RecommendTitle>
                We recommend a {PLANS[recommendedPlan].label} unit
              </RecommendTitle>
              <RecommendBody>
                You can adjust your plan size when we arrive for pickup.
              </RecommendBody>
            </RecommendCopy>
          </RecommendBanner>
        )}

        <div>
          <IllustrationWrap>
            <IllustrationFrame>
              <IllustrationImg
                src={PLAN_ILLUSTRATION[selectedPlan.name]}
                alt={`${selectedPlan.label} storage size`}
                width={314}
                height={240}
              />
            </IllustrationFrame>
          </IllustrationWrap>
          <PriceBlock>
            <Price>
              {monthlyAmount != null
                ? `${formatCurrency(monthlyAmount, {
                    maximumFractionDigits: 0,
                  })}/mo`
                : '—/mo'}
            </Price>
            <PlanCaption>
              {selectedPlan.label} - {selectedPlan.description}
            </PlanCaption>
          </PriceBlock>
        </div>

        <SliderWrap>
          <DesktopSizeHint
            $centerOffset={thumbPlacement.centerOffset}
            $atStart={thumbPlacement.atStart}
            $atEnd={thumbPlacement.atEnd}
          >
            {selectedPlan.label}
          </DesktopSizeHint>
          <SliderTrack>
            <SliderTrackRail>
              <SliderFill $width={thumbPlacement.centerOffset} />
            </SliderTrackRail>
            <SliderTickMarks aria-hidden>
              {Array.from({ length: planCount }).map((_, i) => (
                <SliderTickMark
                  key={i}
                  $offset={sliderThumbCenterOffset(i, planCount)}
                  $withinFill={i <= planIndex}
                />
              ))}
            </SliderTickMarks>
            <SliderThumb
              $centerOffset={thumbPlacement.centerOffset}
              $atStart={thumbPlacement.atStart}
              $atEnd={thumbPlacement.atEnd}
            />
            <SliderInput
              type="range"
              min={0}
              max={planCount - 1}
              step={1}
              value={planIndex}
              aria-label="Storage plan size"
              aria-valuetext={selectedPlan.label}
              onChange={(e) => {
                const next = DIMENSIONAL_PLANS[Number(e.target.value)];
                if (!next) return;
                track({
                  objectName: 'plan_size',
                  objectType: 'slider',
                  label: next.label,
                  value: next.name,
                });
                setPlanSizeFromSlider(next.name);
              }}
              onPointerUp={commitPlanSize}
              onTouchEnd={commitPlanSize}
            />
          </SliderTrack>
          <TickLabels>
            {useMobileLabels
              ? MOBILE_SLIDER_LABEL_NAMES.map((name) => {
                  const labelIndex = DIMENSIONAL_PLANS.findIndex(
                    (plan) => plan.name === name,
                  );
                  const idx = Math.max(0, labelIndex);
                  return (
                    <Tick
                      key={name}
                      $offset={sliderThumbCenterOffset(idx, planCount)}
                    >
                      {PLANS[name].label}
                    </Tick>
                  );
                })
              : DIMENSIONAL_PLANS.map((plan, i) => {
                  const show =
                    i === 0 ||
                    i === DIMENSIONAL_PLANS.length - 1 ||
                    i % 2 === 0;
                  if (!show) return null;
                  return (
                    <Tick
                      key={plan.name}
                      $offset={sliderThumbCenterOffset(i, planCount)}
                    >
                      {plan.label}
                    </Tick>
                  );
                })}
          </TickLabels>
        </SliderWrap>

        {commitment && (
          <Banner>
            <BannerTitle>What’s included in your plan</BannerTitle>
            <InclusionList>
              <FeatureCheck>
                {complimentaryHoursDisplay} complimentary hours for your initial
                pickup and your final delivery.{' '}
                <InlineBrandLink
                  type="button"
                  onClick={() => {
                    track({
                      objectName: 'final_delivery_cost',
                      objectType: 'button',
                      label: 'See more details',
                    });
                    setShowFinalDeliveryCost(true);
                  }}
                >
                  See more details
                </InlineBrandLink>
              </FeatureCheck>
              <FeatureCheck>{additionalPickupLine}</FeatureCheck>
              <FeatureCheck>
                Protection for each of your items based on its weight, valued at
                $1 per pound
              </FeatureCheck>
              <FeatureCheck>
                Wrapping and disassembly of your furniture during pickup
              </FeatureCheck>
              <FeatureCheck>
                Transportation of all boxes and furniture from your home to our
                storage facility
              </FeatureCheck>
              <FeatureCheck>
                Easy access to your items at any time through your Client portal
              </FeatureCheck>
              <FeatureCheck>
                Ongoing pickup and delivery of your items within{' '}
                {serviceAreaLabel}
              </FeatureCheck>
            </InclusionList>
          </Banner>
        )}
      </Stack>
      <StorageCalculatorModal
        isOpen={showEstimator}
        selectedItems={storageCalculatorItems}
        onSelectedItemsChange={(items) =>
          onFlowChange(
            'storageCalculatorItems',
            storageCalculatorItemsFromSelectedItems(items),
          )
        }
        onClose={(plan) => {
          setShowEstimator(false);
          if (!plan) return;
          setRecommendedPlan(plan.name);
          setPlanSizeImmediate(plan.name);
          track({
            action: 'display',
            objectType: 'tooltip',
            objectName: 'recommended_plan_size_tooltip',
            value: plan.name,
          });
        }}
      />
      <FinalDeliveryCostModal
        isOpen={showFinalDeliveryCost}
        onClose={() => setShowFinalDeliveryCost(false)}
      />
    </CardPadded>
  );
};
